import { useState } from 'react'
import {
  Card,
  Form,
  Input,
  Button,
  Avatar,
  message,
  Modal,
  Space,
  Row,
  Col
} from 'antd'
import { UserOutlined, LockOutlined, LogoutOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useUserStore } from '../store/user'
import { userApi } from '../api/user'

export default function UserSettings() {
  const navigate = useNavigate()
  const { token, userInfo, setUserInfo, logout } = useUserStore()
  const [loading, setLoading] = useState(false)
  const [passwordModalVisible, setPasswordModalVisible] = useState(false)
  const [infoForm] = Form.useForm()
  const [passwordForm] = Form.useForm()

  const handleUpdateInfo = async (values: { nickname?: string; avatar?: string }) => {
    setLoading(true)
    try {
      const result = await userApi.updateUserInfo(values)
      setUserInfo(result)
      message.success('更新成功')
    } finally {
      setLoading(false)
    }
  }

  const handleChangePassword = async (values: { oldPassword: string; newPassword: string }) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('两次输入的密码不一致')
      return
    }
    setLoading(true)
    try {
      await userApi.changePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword
      })
      message.success('密码修改成功')
      setPasswordModalVisible(false)
      passwordForm.resetFields()
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    Modal.confirm({
      title: '确定退出登录吗？',
      onOk: () => {
        logout()
        navigate('/login')
        message.success('已退出登录')
      }
    })
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">个人中心</h2>

      <Row gutter={24}>
        {/* 用户基本信息 */}
        <Col xs={24} md={12}>
          <Card title="基本信息" className="mb-6">
            <div className="flex items-center mb-6">
              <Avatar size={80} src={userInfo?.avatar} icon={<UserOutlined />} />
              <div className="ml-4">
                <h3 className="text-xl font-bold">{userInfo?.nickname}</h3>
                <p className="text-gray-500">{userInfo?.username}</p>
              </div>
            </div>

            <Form
              form={infoForm}
              layout="vertical"
              initialValues={{
                nickname: userInfo?.nickname,
                avatar: userInfo?.avatar
              }}
              onFinish={handleUpdateInfo}
            >
              <Form.Item
                label="昵称"
                name="nickname"
                rules={[{ required: true, message: '请输入昵称' }]}
              >
                <Input placeholder="请输入昵称" />
              </Form.Item>

              <Form.Item
                label="头像URL"
                name="avatar"
              >
                <Input placeholder="请输入头像URL（可选）" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  保存修改
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* 账户安全 */}
        <Col xs={24} md={12}>
          <Card title="账户安全" className="mb-6">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="default"
                icon={<LockOutlined />}
                onClick={() => setPasswordModalVisible(true)}
                block
              >
                修改密码
              </Button>

              <Button
                danger
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                block
              >
                退出登录
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 修改密码弹窗 */}
      <Modal
        title="修改密码"
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false)
          passwordForm.resetFields()
        }}
        onOk={() => passwordForm.submit()}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={passwordForm}
          onFinish={handleChangePassword}
          layout="vertical"
        >
          <Form.Item
            label="原密码"
            name="oldPassword"
            rules={[{ required: true, message: '请输入原密码' }]}
          >
            <Input.Password placeholder="请输入原密码" />
          </Form.Item>

          <Form.Item
            label="新密码"
            name="newPassword"
            rules={[{ required: true, message: '请输入新密码' }]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>

          <Form.Item
            label="确认密码"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'))
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
